import { useEffect, useRef, useMemo, Fragment } from 'react'
import { useImmer } from 'use-immer'
import { useSelector } from 'react-redux'
import { selectAdInfo } from 'redux/selector/app'

import { fetchDiscoverUploaderList } from 'api'
import type { SchemaVideoUploaderShow } from 'api'
import { useNavigateTo } from 'hooks/useNavigateTo'

import StaticItem from '../Statictem'
import { TitleBox } from '../../Styled'
import { Wrapper, ScrollWrap, EmptyBox, ScrollList } from './Styled'
import { AdvertisementType } from 'constant/advertisement'
import AdItem from 'page/ViewMore/component/AdItem'
import isEmpty from 'lodash/isEmpty'
import themeAssetloadingDiscoverActorSvg from '@theme/loading/discover-actor.svg'

interface ListInfo {
  nextPage: number
  isLoading: boolean
  hasMore: boolean
  list: SchemaVideoUploaderShow[]
  page_result: { current: number | null; pageSize: number | null; total: number | null }
}

export default function ContentUploader() {
  const { navigateToViewUploader } = useNavigateTo()
  const adInfo = useSelector(selectAdInfo)
  const adCategory = adInfo?.[AdvertisementType.Category] || []

  const [listInfo, updateListInfo] = useImmer<ListInfo>({
    nextPage: 1,
    isLoading: true,
    hasMore: true,
    list: [],
    page_result: { current: null, pageSize: null, total: null },
  })

  const hasNoContent = listInfo.list.length === 0 && !listInfo.isLoading

  // 防重複請求：記錄目前 in-flight 的 page
  const currentFetching = useRef<{ page: number | null }>({ page: null })
  // unmount 守衛：切 TAB / 離開頁後不再 setState
  const ignoreRef = useRef(false)
  // 最近一次請求的 controller，unmount 時中斷
  const controllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    ignoreRef.current = false
    return () => {
      ignoreRef.current = true
      controllerRef.current?.abort()
    }
  }, [])

  const handleFetchUploaderList = () => {
    if (currentFetching.current.page === listInfo.nextPage || !listInfo.hasMore) {
      return
    }
    currentFetching.current = { page: listInfo.nextPage }

    const controller = new AbortController()
    controllerRef.current = controller

    fetchDiscoverUploaderList({ current: listInfo.nextPage, pageSize: 30, signal: controller.signal })
      .then((response) => {
        if (ignoreRef.current || controller.signal.aborted) return
        updateListInfo((draft) => {
          draft.list = draft.list.concat(response.data ?? [])
          draft.hasMore = response.page_result.total > response.page_result.current * response.page_result.pageSize
          draft.nextPage = response.page_result.current + 1
          draft.page_result = response.page_result
          draft.isLoading = false
        })
      })
      .catch(() => {
        if (ignoreRef.current || controller.signal.aborted) return
        // 錯誤時停止自動載入（hasMore=false），避免 InfiniteScroll 於持續失敗時無限重打 API；重新進入分頁會重載
        updateListInfo((draft) => {
          draft.isLoading = false
          draft.hasMore = false
        })
      })
  }

  // 同演员分頁邏輯（ContentActor:139-140 前6筆＝推薦、其餘＝全部）：
  // recommend===1 置前、再依 seqno(數字大優先) 排序，之後以「位置」劃分——前 6 筆顯示為「推荐UP主」，
  // index >= 6 為「全部UP主」。因此當推薦數 > 6 時，第 7 個(含)之後的推薦會落入「全部」（QA 指定行為）。
  // 排序後仍以 item.id 為 key，避免後頁載入時已渲染卡片 remount / 頭像重載。
  const sortedList = useMemo(() => {
    const rank = (u: SchemaVideoUploaderShow) => (u?.recommend === 1 ? 1 : 0)
    return [...listInfo.list].sort((a, b) => {
      if (rank(a) !== rank(b)) return rank(b) - rank(a)
      return (b?.seqno ?? 0) - (a?.seqno ?? 0)
    })
  }, [listInfo.list])

  const renderUploaderCard = (item: SchemaVideoUploaderShow) => (
    <StaticItem
      key={item.id}
      name={item.name}
      icon_path={item.icon_path}
      onClick={() => navigateToViewUploader({ uploaderId: item.id })}
      placeholder={themeAssetloadingDiscoverActorSvg}
      noLazy={false}
      showName
    />
  )

  return (
    <Wrapper>
      {hasNoContent && <EmptyBox />}
      <ScrollWrap>
        <ScrollList pageStart={0} loadMore={handleFetchUploaderList} hasMore={listInfo?.hasMore} useWindow={false}>
          {sortedList.map((item, i) => {
            // 廣告只穿插在「全部UP主」區塊（i >= 6），每 12 筆一則（同演员 ContentActor:132-134）
            const needToShowAd = i >= 6 && (i - 6 + 1) % 12 === 0 && adCategory.length > 0
            const adIndex = ~~((i - 6 - 11) / 12)
            const wrappedIndex = adCategory.length > 0 ? adIndex % adCategory.length : 0
            return (
              <Fragment key={item.id}>
                {/* 前 6 筆＝推薦、其餘＝全部；推薦數 > 6 時溢位者落入全部 */}
                {i === 0 && <TitleBox>推荐UP主</TitleBox>}
                {i === 6 && <TitleBox>全部UP主</TitleBox>}
                {renderUploaderCard(item)}
                {needToShowAd && !isEmpty(adCategory[wrappedIndex]) && <AdItem {...adCategory[wrappedIndex]} />}
              </Fragment>
            )
          })}
        </ScrollList>
      </ScrollWrap>
    </Wrapper>
  )
}
